import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../api/api';
import axiosInstance from '../api/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Users,
  Briefcase,
  FileText,
  MessageSquare,
  Star,
  DollarSign,
  TrendingUp,
  Activity,
  Search,
  Filter,
  MoreVertical,
  UserCheck,
  UserX,
  Trash2,
  Eye,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Settings,
  LogOut,
  Wallet,
  Ban,
  FileWarning
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import OverviewTab from '../components/admin-dashboard/OverviewTab';
import TestAsCard from '../components/admin-dashboard/TestAsCard';
import BuddyInsightsCard from '../components/admin-dashboard/BuddyInsightsCard';

const VIDEO_LABELS = {
  'landing-promo': 'Landing Page Promo',
  'landing-cartoon': 'Landing Page Cartoon',
  'features-promo': 'Features Page Promo',
  'contractor-funnel-promo': 'Contractor Funnel Promo',
};

const VideoStatsCard = () => {
  const [videos, setVideos] = React.useState([]);
  React.useEffect(() => {
    axiosInstance.get('/analytics/video-stats').then((r) => setVideos(r.data.videos || [])).catch(() => {});
  }, []);
  return (
    <div className="mt-6 bg-white dark:bg-neutral-800 border-2 border-black p-5 shadow-[4px_4px_0px_#000]" data-testid="video-stats-card">
      <h3 className="font-black uppercase text-sm mb-3 dark:text-white">🎬 Video Views</h3>
      {videos.length === 0 ? (
        <p className="text-sm text-neutral-400 font-bold">No video plays recorded yet — stats appear as visitors watch your promo videos.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-black uppercase text-neutral-500 border-b-2 border-neutral-200">
                <th className="py-2 pr-3">Video</th>
                <th className="py-2 pr-3 text-right">Plays</th>
                <th className="py-2 pr-3 text-right">Completed</th>
                <th className="py-2 text-right">Completion</th>
              </tr>
            </thead>
            <tbody>
              {videos.map((v) => (
                <tr key={v.video_id} className="border-b border-neutral-100" data-testid={`video-stat-${v.video_id}`}>
                  <td className="py-2 pr-3 font-bold dark:text-white">{VIDEO_LABELS[v.video_id] || v.video_id}</td>
                  <td className="py-2 pr-3 text-right font-black dark:text-white">{v.plays}</td>
                  <td className="py-2 pr-3 text-right font-black text-emerald-700">{v.completes}</td>
                  <td className="py-2 text-right font-black">{v.completion_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
import UsersTab from '../components/admin-dashboard/UsersTab';
import JobsTab from '../components/admin-dashboard/JobsTab';
import EscrowsTab from '../components/admin-dashboard/EscrowsTab';
import ReviewsTab from '../components/admin-dashboard/ReviewsTab';
import ActivityTab from '../components/admin-dashboard/ActivityTab';
import AuditLogTab from '../components/admin-dashboard/AuditLogTab';
import TrafficTab from '../components/admin-dashboard/TrafficTab';
import { EscrowDetailModal, ResolveDisputeModal } from '../components/admin-dashboard/EscrowModals';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activities, setActivities] = useState([]);
  const [escrows, setEscrows] = useState([]);
  const [escrowStats, setEscrowStats] = useState(null);
  const [escrowsPage, setEscrowsPage] = useState(1);
  const [escrowsTotalPages, setEscrowsTotalPages] = useState(1);
  const [escrowStatusFilter, setEscrowStatusFilter] = useState('all');
  const [selectedEscrow, setSelectedEscrow] = useState(null);
  const [showEscrowModal, setShowEscrowModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveType, setResolveType] = useState('release_to_worker');
  const [splitPercentage, setSplitPercentage] = useState(50);
  const [adminNote, setAdminNote] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [jobsPage, setJobsPage] = useState(1);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [jobsTotalPages, setJobsTotalPages] = useState(1);
  const [reviewsTotalPages, setReviewsTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [jobStatusFilter, setJobStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 403) {
        toast({
          title: 'Access Denied',
          description: 'You do not have admin privileges',
          variant: 'destructive'
        });
        navigate('/');
      }
    }
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await adminAPI.getUsers({
        page: usersPage,
        limit: 10,
        search: searchQuery || undefined,
        user_type: userTypeFilter !== 'all' ? userTypeFilter : undefined
      });
      setUsers(response.data.users);
      setUsersTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [usersPage, searchQuery, userTypeFilter]);

  const fetchJobs = useCallback(async () => {
    try {
      const response = await adminAPI.getJobs({
        page: jobsPage,
        limit: 10,
        search: searchQuery || undefined,
        status: jobStatusFilter !== 'all' ? jobStatusFilter : undefined
      });
      setJobs(response.data.jobs);
      setJobsTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  }, [jobsPage, searchQuery, jobStatusFilter]);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await adminAPI.getReviews({ page: reviewsPage, limit: 10 });
      setReviews(response.data.reviews);
      setReviewsTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  }, [reviewsPage]);

  const fetchActivity = useCallback(async () => {
    try {
      const response = await adminAPI.getActivity(20);
      setActivities(response.data.activities);
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  }, []);

  const fetchEscrows = useCallback(async () => {
    try {
      const response = await adminAPI.getEscrows({
        page: escrowsPage,
        limit: 10,
        status: escrowStatusFilter !== 'all' ? escrowStatusFilter : undefined,
        search: searchQuery || undefined
      });
      setEscrows(response.data.escrows);
      setEscrowsTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Error fetching escrows:', error);
    }
  }, [escrowsPage, escrowStatusFilter, searchQuery]);

  const fetchEscrowStats = useCallback(async () => {
    try {
      const response = await adminAPI.getEscrowStats();
      setEscrowStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching escrow stats:', error);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    setLoading(true);
    Promise.all([fetchStats(), fetchActivity()]).finally(() => setLoading(false));
    axiosInstance.get('/contact/unread-count')
      .then((r) => setUnreadMessages(r.data.unread || 0))
      .catch(() => {});
  }, [navigate, fetchStats, fetchActivity]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
  }, [activeTab, fetchUsers]);

  useEffect(() => {
    if (activeTab === 'jobs') fetchJobs();
  }, [activeTab, fetchJobs]);

  useEffect(() => {
    if (activeTab === 'reviews') fetchReviews();
  }, [activeTab, fetchReviews]);

  useEffect(() => {
    if (activeTab === 'escrows') {
      fetchEscrows();
      fetchEscrowStats();
    }
  }, [activeTab, fetchEscrows, fetchEscrowStats]);

  const handleSuspendUser = async (userId, currentlySuspended) => {
    setActionLoading(userId);
    try {
      await adminAPI.suspendUser(userId, !currentlySuspended);
      toast({
        title: 'Success',
        description: `User ${currentlySuspended ? 'unsuspended' : 'suspended'} successfully`
      });
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update user',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const DASHBOARD_BY_ROLE = {
    worker: '/worker-dashboard',
    contractor: '/contractor-dashboard',
    homeowner: '/homeowner-dashboard',
  };

  const handleImpersonate = async (userId, userName) => {
    if (!window.confirm(`Test the app as ${userName}? You'll get their view for up to 1 hour. Use the red banner to return to admin.`)) return;
    setActionLoading(userId);
    try {
      const res = await adminAPI.impersonate(userId);
      const { token, user } = res.data;
      // Backup admin session for one-click return
      localStorage.setItem('admin_impersonation_backup', JSON.stringify({
        token: localStorage.getItem('token'),
        userType: localStorage.getItem('userType'),
        userId: localStorage.getItem('userId'),
        user: localStorage.getItem('user'),
      }));
      localStorage.setItem('token', token);
      localStorage.setItem('userType', user.user_type);
      localStorage.setItem('userId', user._id);
      localStorage.setItem('user', JSON.stringify(user));
      window.location.href = DASHBOARD_BY_ROLE[user.user_type] || '/';
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to start test session',
        variant: 'destructive',
      });
      setActionLoading(null);
    }
  };

  const handleToggleAdmin = async (userId, isAdmin, userName) => {    const confirmMsg = isAdmin
      ? `Remove admin access from ${userName}? They'll be restored to their previous role.`
      : `Promote ${userName} to ADMIN? They'll have full access to the admin dashboard.`;
    if (!window.confirm(confirmMsg)) return;
    setActionLoading(userId);
    try {
      const res = await adminAPI.setAdminRole(userId, !isAdmin);
      toast({ title: 'Success', description: res.data.message });
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update admin role',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyUser = async (userId, currentlyVerified) => {
    setActionLoading(userId);
    try {
      await adminAPI.verifyUser(userId, !currentlyVerified);
      toast({
        title: 'Success',
        description: `User ${currentlyVerified ? 'unverified' : 'verified'} successfully`
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update user',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    setActionLoading(userId);
    try {
      await adminAPI.deleteUser(userId);
      toast({
        title: 'Success',
        description: 'User deleted successfully'
      });
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete user',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }
    setActionLoading(jobId);
    try {
      await adminAPI.deleteJob(jobId);
      toast({
        title: 'Success',
        description: 'Job deleted successfully'
      });
      fetchJobs();
      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete job',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }
    setActionLoading(reviewId);
    try {
      await adminAPI.deleteReview(reviewId);
      toast({
        title: 'Success',
        description: 'Review deleted successfully'
      });
      fetchReviews();
      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete review',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewEscrow = async (escrow) => {
    setSelectedEscrow(escrow);
    setShowEscrowModal(true);
  };

  const handleResolveDispute = async () => {
    if (!selectedEscrow) return;
    setActionLoading(selectedEscrow.id);
    try {
      await adminAPI.resolveDispute(selectedEscrow.id, {
        resolution_type: resolveType,
        split_percentage: splitPercentage,
        note: adminNote
      });
      toast({
        title: 'Success',
        description: 'Dispute resolved successfully'
      });
      setShowResolveModal(false);
      setShowEscrowModal(false);
      setAdminNote('');
      fetchEscrows();
      fetchEscrowStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to resolve dispute',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleForceRelease = async (escrowId) => {
    const note = prompt('Enter a reason for force releasing this escrow:');
    if (!note) return;
    
    setActionLoading(escrowId);
    try {
      await adminAPI.forceReleaseEscrow(escrowId, note);
      toast({
        title: 'Success',
        description: 'Escrow force released to worker'
      });
      fetchEscrows();
      fetchEscrowStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to force release escrow',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelEscrow = async (escrowId) => {
    const note = prompt('Enter a reason for cancelling this escrow:');
    if (!note) return;
    
    if (!window.confirm('Are you sure you want to cancel this escrow and refund the contractor?')) {
      return;
    }
    
    setActionLoading(escrowId);
    try {
      await adminAPI.cancelEscrow(escrowId, note);
      toast({
        title: 'Success',
        description: 'Escrow cancelled and marked for refund'
      });
      fetchEscrows();
      fetchEscrowStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to cancel escrow',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'traffic', label: 'Traffic', icon: TrendingUp },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'jobs', label: 'Jobs', icon: Briefcase },
    { id: 'escrows', label: 'Escrows', icon: Wallet },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'audit', label: 'Audit Log', icon: FileText },
  ];

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'new_user': return <Users className="h-4 w-4 text-blue-500" />;
      case 'new_job': return <Briefcase className="h-4 w-4 text-green-500" />;
      case 'new_application': return <FileText className="h-4 w-4 text-purple-500" />;
      default: return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#2B4C6F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600 dark:text-neutral-400 font-bold uppercase">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-[#0F172A] border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-red-500 border-2 border-white flex items-center justify-center shrink-0">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white uppercase">Admin Dashboard</h1>
                <p className="text-neutral-400 text-sm">Platform Management</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="admin-header-actions">
            <Button 
              variant="outline" 
              className="text-white border-2 border-white hover:bg-white/10 rounded-none font-bold text-xs sm:text-sm px-2.5 sm:px-4"
              onClick={() => navigate('/admin/lead-hunter')}
              data-testid="admin-leadhunter-link"
            >
              🎯 Lead Hunter
            </Button>
            <Button 
              variant="outline" 
              className="text-white border-2 border-white hover:bg-white/10 rounded-none font-bold text-xs sm:text-sm px-2.5 sm:px-4"
              onClick={() => navigate('/admin/outreach')}
              data-testid="admin-outreach-link"
            >
              🤝 AI Outreach
            </Button>
            <Button 
              variant="outline" 
              className="text-white border-2 border-white hover:bg-white/10 rounded-none font-bold text-xs sm:text-sm px-2.5 sm:px-4"
              onClick={() => navigate('/admin/ads')}
              data-testid="admin-ads-link"
            >
              📣 Ads
            </Button>
            <Button 
              variant="outline" 
              className="text-white border-2 border-white hover:bg-white/10 rounded-none font-bold text-xs sm:text-sm px-2.5 sm:px-4"
              onClick={() => navigate('/admin/store')}
              data-testid="admin-store-link"
            >
              🛒 Store
            </Button>
            <Button 
              variant="outline" 
              className="text-white border-2 border-white hover:bg-white/10 rounded-none font-bold text-xs sm:text-sm px-2.5 sm:px-4"
              onClick={() => navigate('/admin/chief')}
              data-testid="admin-chief-link"
            >
              🤖 Chief of Staff
            </Button>
            <Button 
              variant="outline" 
              className="relative text-white border-2 border-white hover:bg-white/10 rounded-none font-bold text-xs sm:text-sm px-2.5 sm:px-4"
              onClick={() => navigate('/admin/messages')}
              data-testid="admin-messages-link"
            >
              📨 Inbox
              {unreadMessages > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 bg-red-500 border-2 border-white text-white text-[11px] font-black rounded-full flex items-center justify-center" data-testid="inbox-unread-dot">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </Button>
            <Button 
              variant="outline" 
              className="text-white border-2 border-white hover:bg-white/10 rounded-none font-bold text-xs sm:text-sm px-2.5 sm:px-4"
              onClick={() => navigate('/admin/subscriptions')}
              data-testid="admin-subscriptions-link"
            >
              💰 Subscriptions
            </Button>
            <Button 
              variant="outline" 
              className="text-white border-2 border-white hover:bg-white/10 rounded-none font-bold text-xs sm:text-sm px-2.5 sm:px-4"
              onClick={() => navigate('/')}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Exit Admin
            </Button>
            </div>
          </div>
        </div>
      </div>

      {/* View-As strip — quick admin access to role dashboards */}
      <div className="bg-black border-b-4 border-[#D95800]" data-testid="admin-view-as-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-2 md:gap-3">
          <span className="text-[10px] md:text-xs font-black uppercase tracking-widest text-[#D95800] mr-1">
            View As →
          </span>
          <button
            onClick={() => navigate('/contractor-dashboard')}
            className="inline-flex items-center gap-1.5 h-8 md:h-9 px-3 bg-[#2B4C6F] text-white font-black uppercase text-[10px] md:text-xs tracking-widest border-2 border-white shadow-[2px_2px_0px_#fff] hover:shadow-[3px_3px_0px_#fff] active:translate-y-0.5 active:shadow-none rounded-none transition-all"
            data-testid="view-as-contractor-btn"
          >
            Contractor Dashboard
          </button>
          <button
            onClick={() => navigate('/worker-dashboard')}
            className="inline-flex items-center gap-1.5 h-8 md:h-9 px-3 bg-emerald-500 text-white font-black uppercase text-[10px] md:text-xs tracking-widest border-2 border-white shadow-[2px_2px_0px_#fff] hover:shadow-[3px_3px_0px_#fff] active:translate-y-0.5 active:shadow-none rounded-none transition-all"
            data-testid="view-as-worker-btn"
          >
            Worker Dashboard
          </button>
          <button
            onClick={() => navigate('/homeowner-dashboard')}
            className="inline-flex items-center gap-1.5 h-8 md:h-9 px-3 bg-amber-500 text-white font-black uppercase text-[10px] md:text-xs tracking-widest border-2 border-white shadow-[2px_2px_0px_#fff] hover:shadow-[3px_3px_0px_#fff] active:translate-y-0.5 active:shadow-none rounded-none transition-all"
            data-testid="view-as-homeowner-btn"
          >
            Homeowner Dashboard
          </button>
          <span className="hidden md:inline text-white/30 mx-1">|</span>
          <button
            onClick={() => navigate('/contractor-control-panel')}
            className="inline-flex items-center gap-1.5 h-8 md:h-9 px-3 bg-white text-black font-black uppercase text-[10px] md:text-xs tracking-widest border-2 border-white hover:bg-neutral-200 active:translate-y-0.5 rounded-none transition-all"
            data-testid="view-as-contractor-cp-btn"
          >
            Contractor Settings
          </button>
          <button
            onClick={() => navigate('/worker-control-panel')}
            className="inline-flex items-center gap-1.5 h-8 md:h-9 px-3 bg-white text-black font-black uppercase text-[10px] md:text-xs tracking-widest border-2 border-white hover:bg-neutral-200 active:translate-y-0.5 rounded-none transition-all"
            data-testid="view-as-worker-cp-btn"
          >
            Worker Settings
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[4px_4px_0px_#000] p-4">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 transition-colors border-2 ${
                      activeTab === tab.id
                        ? 'bg-[#2B4C6F] text-white border-black font-bold'
                        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 border-transparent'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <tab.icon className="h-5 w-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeTab === 'overview' && (
              <>
                <OverviewTab stats={stats} navigate={navigate} />
                <TestAsCard handleImpersonate={handleImpersonate} />
                <BuddyInsightsCard />
                <VideoStatsCard />
              </>
            )}
            {activeTab === 'traffic' && <TrafficTab />}
            {activeTab === 'users' && (
              <UsersTab
                users={users}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                userTypeFilter={userTypeFilter}
                setUserTypeFilter={setUserTypeFilter}
                fetchUsers={fetchUsers}
                formatDate={formatDate}
                actionLoading={actionLoading}
                handleVerifyUser={handleVerifyUser}
                handleSuspendUser={handleSuspendUser}
                handleDeleteUser={handleDeleteUser}
                handleToggleAdmin={handleToggleAdmin}
                handleImpersonate={handleImpersonate}
                usersPage={usersPage}
                usersTotalPages={usersTotalPages}
                setUsersPage={setUsersPage}
              />
            )}
            {activeTab === 'jobs' && (
              <JobsTab
                jobs={jobs}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                jobStatusFilter={jobStatusFilter}
                setJobStatusFilter={setJobStatusFilter}
                fetchJobs={fetchJobs}
                formatDate={formatDate}
                actionLoading={actionLoading}
                navigate={navigate}
                handleDeleteJob={handleDeleteJob}
                jobsPage={jobsPage}
                jobsTotalPages={jobsTotalPages}
                setJobsPage={setJobsPage}
              />
            )}
            {activeTab === 'escrows' && (
              <EscrowsTab
                escrowStats={escrowStats}
                escrows={escrows}
                escrowStatusFilter={escrowStatusFilter}
                setEscrowStatusFilter={setEscrowStatusFilter}
                setEscrowsPage={setEscrowsPage}
                fetchEscrows={fetchEscrows}
                fetchEscrowStats={fetchEscrowStats}
                formatDate={formatDate}
                actionLoading={actionLoading}
                handleViewEscrow={handleViewEscrow}
                setSelectedEscrow={setSelectedEscrow}
                setShowResolveModal={setShowResolveModal}
                handleForceRelease={handleForceRelease}
                handleCancelEscrow={handleCancelEscrow}
                escrowsPage={escrowsPage}
                escrowsTotalPages={escrowsTotalPages}
              />
            )}
            {activeTab === 'reviews' && (
              <ReviewsTab
                reviews={reviews}
                fetchReviews={fetchReviews}
                formatDate={formatDate}
                actionLoading={actionLoading}
                handleDeleteReview={handleDeleteReview}
                reviewsPage={reviewsPage}
                reviewsTotalPages={reviewsTotalPages}
                setReviewsPage={setReviewsPage}
              />
            )}
            {activeTab === 'activity' && (
              <ActivityTab
                activities={activities}
                fetchActivity={fetchActivity}
                formatDate={formatDate}
                getActivityIcon={getActivityIcon}
              />
            )}
            {activeTab === 'audit' && <AuditLogTab />}
          </div>
        </div>
      </div>

      {/* Modals */}
      <EscrowDetailModal
        open={showEscrowModal}
        onOpenChange={setShowEscrowModal}
        selectedEscrow={selectedEscrow}
        formatDate={formatDate}
        setShowResolveModal={setShowResolveModal}
      />
      <ResolveDisputeModal
        open={showResolveModal}
        onOpenChange={setShowResolveModal}
        resolveType={resolveType}
        setResolveType={setResolveType}
        splitPercentage={splitPercentage}
        setSplitPercentage={setSplitPercentage}
        adminNote={adminNote}
        setAdminNote={setAdminNote}
        handleResolveDispute={handleResolveDispute}
        actionLoading={actionLoading}
      />
    </div>
  );
};

export default AdminDashboard;
